package one.healthium.profile.ui.avatar

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import ifaandroid.feature.profile.profile_ui.generated.resources.Res
import ifaandroid.feature.profile.profile_ui.generated.resources.ic_vr_glasses_48
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import loco.GENERAL_CANCEL
import loco.USER_AVATAR_DELETE_CONFIRMATION_MESSAGE
import loco.USER_AVATAR_DELETE_CONFIRMATION_POSITIVE_BUTTON
import loco.USER_AVATAR_DELETE_CONFIRMATION_TITLE
import one.healthium.profile.ui.avatar.components.AppImagePicker
import one.healthium.profile.ui.avatar.components.ConfirmationSheet
import one.healthium.profile.ui.avatar.components.toByteArray
import one.healthium.ui.NetworkImage
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppButtonDefaults
import one.healthium.ui.compose.components.AppIconButton
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun UserAvatar(
    modifier: Modifier = Modifier,
    size: Dp? = null,
) {
    val viewModel = koinViewModel<UserAvatarViewModel>()
    val state by viewModel.state.collectAsState()

    AvatarScreenContent(
        modifier = modifier,
        state = state,
        size = size,
        onAvatarUpdated = viewModel::onAvatarUpdated,
        onDeleteAvatarClicked = viewModel::onDeleteAvatarClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun AvatarScreenContent(
    modifier: Modifier = Modifier,
    size: Dp? = null,
    state: UserAvatarUiState,
    onAvatarUpdated: (ByteArray) -> Unit,
    onDeleteAvatarClicked: () -> Unit,
) {
    var selectedImage by remember { mutableStateOf<ImageBitmap?>(null) }
    val openImagePicker = remember { mutableStateOf(value = false) }
    val showDeleteConfirmation = remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    AppImagePicker(
        showPicker = openImagePicker,
        selectedImageCallback = {
            selectedImage = it
            it.toByteArray()?.let { p1 -> onAvatarUpdated(p1) }
        },
        canDelete = state.userAvatarUrl != null,
        onDeleteImageClicked = {
            scope.launch {
                delay(300)
                showDeleteConfirmation.value = true
            }
        },
    )

    AppIconButton(
        modifier = modifier,
        size = size ?: 84.dp,
        colors = AppButtonDefaults.appButtonColors(
            containerColor = AppTheme.profileStyle.avatarContainerColorOrDefault,
            contentColor = AppTheme.profileStyle.avatarContentColorOrDefault,
        ),
        onClick = { openImagePicker.value = true },
    ) {
        if (selectedImage == null && state.userAvatarUrl == null) {
            Icon(
                painter = painterResource(Res.drawable.ic_vr_glasses_48),
                tint = white,
                contentDescription = null,
            )
        }
        if (selectedImage != null) {
            Image(
                modifier = Modifier.fillMaxSize().clip(CircleShape),
                bitmap = selectedImage!!,
                contentDescription = null,
                contentScale = ContentScale.Crop,
            )
        }
        if (state.userAvatarUrl != null) {
            NetworkImage(
                modifier = Modifier.fillMaxSize().clip(CircleShape),
                imageUrl = state.userAvatarUrl,
                contentScale = ContentScale.Crop,
                onLoaded = {
                    selectedImage = null
                }
            )
        }
        AppLoadingScreen(
            visible = state.loading,
            dim = true,
            progressIndicatorSize = 24.dp,
            progressIndicatorStrokeWidth = 2.dp,
        )
    }

    if (showDeleteConfirmation.value) {
        ConfirmationSheet(
            title = stringResource(loco.Res.string.USER_AVATAR_DELETE_CONFIRMATION_TITLE),
            message = stringResource(loco.Res.string.USER_AVATAR_DELETE_CONFIRMATION_MESSAGE),
            positiveText = stringResource(loco.Res.string.USER_AVATAR_DELETE_CONFIRMATION_POSITIVE_BUTTON),
            negativeText = stringResource(loco.Res.string.GENERAL_CANCEL),
            onDismissed = { showDeleteConfirmation.value = false },
            onPositiveClicked = {
                onDeleteAvatarClicked()
            },
            onNegativeClicked = {}
        )
    }
}

@Composable
@Preview(showBackground = true)
private fun AvatarScreenPreview() {
    AppTheme {
        AvatarScreenContent(
            state = UserAvatarUiState(),
            onAvatarUpdated = {},
            onDeleteAvatarClicked = {}
        )
    }
}
